
import React from 'react';
import { InsuranceAnalysisResult, Language } from '@/types/insurance';
import { Button } from '@/components/ui/button';
import { Download, FileSignature, Check, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface InsuranceDetailsPanelProps {
  title: string;
  analysisResult?: InsuranceAnalysisResult | null;
  onDownload?: () => void;
  onSign?: () => void;
  isSigned?: boolean;
  signedDocUrl?: string | null;
  requiresSignature?: boolean;
  language?: Language;
}

export const InsuranceDetailsPanel: React.FC<InsuranceDetailsPanelProps> = ({
  title,
  analysisResult,
  onDownload,
  onSign,
  isSigned = false,
  signedDocUrl,
  requiresSignature = false,
  language = 'de'
}) => {
  // Set of translations
  const translations = {
    en: {
      coverageTitle: 'Coverage',
      premiumTitle: 'Premium',
      durationTitle: 'Duration',
      benefitsTitle: 'Benefits',
      exclusionsTitle: 'Exclusions',
      summaryTitle: 'Summary',
      keyFactsTitle: 'Key Facts',
      downloadBtn: 'Download Document',
      signBtn: 'Sign Document',
      signedText: 'Document Signed',
      viewSignedBtn: 'View Signed Document',
      analysisTitle: 'Insurance Analysis',
      noAnalysisText: 'No detailed analysis available for this document.'
    },
    de: {
      coverageTitle: 'Deckung',
      premiumTitle: 'Prämie',
      durationTitle: 'Laufzeit',
      benefitsTitle: 'Leistungen',
      exclusionsTitle: 'Ausschlüsse',
      summaryTitle: 'Zusammenfassung',
      keyFactsTitle: 'Wichtige Fakten',
      downloadBtn: 'Dokument herunterladen',
      signBtn: 'Dokument unterschreiben',
      signedText: 'Dokument unterschrieben',
      viewSignedBtn: 'Unterschriebenes Dokument ansehen',
      analysisTitle: 'Versicherungsanalyse',
      noAnalysisText: 'Für dieses Dokument ist keine detaillierte Analyse verfügbar.'
    }
  };
  
  const t = translations[language];

  // Helper function to format analysis text sections
  const formatSection = (text?: string) => {
    if (!text) return null;
    
    return (
      <div className="prose prose-sm max-w-none" 
        dangerouslySetInnerHTML={{ 
          __html: text
            .replace(/\n/g, '<br>')
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
        }} 
      />
    );
  };

  // Format benefits and exclusions lists
  const formatList = (items?: string[]) => {
    if (!items || !items.length) return null;
    
    return (
      <ul className="list-disc ml-5 space-y-1 text-gray-600">
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  };

  // Extract key facts from analysis
  const extractKeyFacts = () => {
    if (!analysisResult) return [];
    
    const facts = [];
    
    if (analysisResult.premium) {
      const premiumMatch = analysisResult.premium.match(/(\d+[.,]?\d*)\s*(CHF|EUR|USD|€|\$)/i);
      if (premiumMatch) {
        facts.push({
          label: t.premiumTitle,
          value: `${premiumMatch[0]}`
        });
      }
    }
    
    if (analysisResult.duration) {
      const durationMatch = analysisResult.duration.match(/(\d+)\s*(Jahr(e)?|Year(s)?|Monat(e)?|Month(s)?)/i);
      if (durationMatch) {
        facts.push({
          label: t.durationTitle,
          value: durationMatch[0]
        });
      }
    }
    
    if (analysisResult.coverage) {
      const coverageMatch = analysisResult.coverage.match(/(\d+[.,]?\d*)\s*(CHF|EUR|USD|€|\$)/i);
      if (coverageMatch) {
        facts.push({
          label: t.coverageTitle,
          value: `${coverageMatch[0]}`
        });
      }
    }
    
    // Add benefits count if available
    if (analysisResult.benefits && analysisResult.benefits.length > 0) {
      facts.push({
        label: t.benefitsTitle,
        value: `${analysisResult.benefits.length}`
      });
    }
    
    return facts;
  };
  
  const keyFacts = extractKeyFacts();

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4 text-gray-800">{title}</h1>
      
      {/* Key facts section */}
      {keyFacts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {keyFacts.map((fact, index) => (
            <Card key={index} className="p-3 bg-indigo-50 border-indigo-100">
              <div className="text-xs text-indigo-500 font-medium mb-1">{fact.label}</div>
              <div className="text-lg font-semibold text-gray-800">{fact.value}</div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {onDownload && (
          <Button 
            onClick={onDownload}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t.downloadBtn}
          </Button>
        )}
        
        {requiresSignature && !isSigned && onSign && (
          <Button 
            onClick={onSign}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
          >
            <FileSignature className="h-4 w-4" />
            {t.signBtn}
          </Button>
        )}
        
        {isSigned && signedDocUrl && (
          <Button
            variant="outline" 
            className="flex items-center gap-2 border-green-500 text-green-600 hover:bg-green-50"
            onClick={() => window.open(signedDocUrl, '_blank')}
          >
            <Check className="h-4 w-4" />
            {t.viewSignedBtn}
          </Button>
        )}
      </div>
      
      {/* The analysis section */}
      {analysisResult ? (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold border-b pb-2 mb-4">{t.analysisTitle}</h2>
          
          {/* Coverage */}
          {analysisResult.coverage && (
            <div className="mb-5">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{t.coverageTitle}</h3>
              {formatSection(analysisResult.coverage)}
            </div>
          )}
          
          {/* Premium */}
          {analysisResult.premium && (
            <div className="mb-5">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{t.premiumTitle}</h3>
              {formatSection(analysisResult.premium)}
            </div>
          )}
          
          {/* Duration */}
          {analysisResult.duration && (
            <div className="mb-5">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{t.durationTitle}</h3>
              {formatSection(analysisResult.duration)}
            </div>
          )}
          
          {/* Benefits */}
          {analysisResult.benefits && analysisResult.benefits.length > 0 && (
            <div className="mb-5">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{t.benefitsTitle}</h3>
              {formatList(analysisResult.benefits)}
            </div>
          )}
          
          {/* Exclusions */}
          {analysisResult.exclusions && analysisResult.exclusions.length > 0 && (
            <div className="mb-5">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{t.exclusionsTitle}</h3>
              {formatList(analysisResult.exclusions)}
            </div>
          )}
          
          {/* Summary */}
          {analysisResult.summary && (
            <div className="mb-5 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{t.summaryTitle}</h3>
              {formatSection(analysisResult.summary)}
            </div>
          )}
          
          {/* General Analysis */}
          {analysisResult.analysis && !analysisResult.coverage && !analysisResult.premium && !analysisResult.duration && (
            <div className="prose prose-sm max-w-none">
              {formatSection(analysisResult.analysis)}
            </div>
          )}
        </div>
      ) : (
        <div className="py-8 text-center text-gray-500">
          <AlertTriangle className="h-12 w-12 mx-auto text-amber-400 mb-3" />
          <p>{t.noAnalysisText}</p>
        </div>
      )}
    </div>
  );
};
